import { FC } from 'react';
import { ServerError } from '@apollo/client';
import { ApolloError } from '@apollo/client/errors';
import { Center, Stack, Text } from '@chakra-ui/react';
import { CiFolderOff } from 'react-icons/ci';
import { ObjectNotFound } from '../../components/object-not-found/object-not-found';

const isUnauthorizedError = (error?: ApolloError) =>
  error?.message === 'unauthorized';

const isInvalidCedantError = (error?: ApolloError): boolean => {
  if (!error?.networkError) return false;

  // The API will return a 422 status code if the cedant is invalid
  const isServerError = 'statusCode' in error.networkError;
  if (isServerError) {
    return (error.networkError as ServerError).statusCode === 422;
  }

  return false;
};

type DirectoryErrorStateProps = { error?: ApolloError };

export const DirectoryErrorState: FC<DirectoryErrorStateProps> = ({
  error,
}) => {
  const testId = 'directory-error-state';

  // The user doesn't have the correct role (treaty-specific) to access the directory
  if (isUnauthorizedError(error)) {
    return (
      <Center flex={1} data-testid={testId}>
        <ObjectNotFound
          icon={CiFolderOff}
          title="Unauthorized"
          subtitle={
            <Stack>
              <Text>
                Your account doesn't have permission to access to this
                directory.
              </Text>
              <Text>
                Please contact an administrator for further assistance.
              </Text>
            </Stack>
          }
        />
      </Center>
    );
  }

  // The user used an invalid cedant in the URL
  if (isInvalidCedantError(error)) {
    return (
      <Center flex={1} data-testid={testId}>
        <ObjectNotFound
          icon={CiFolderOff}
          title="Treaty not found"
          subtitle={
            <Stack>
              <Text>The treaty you're attempting to access doesn't exist.</Text>
              <Text>
                Please check the URL and try again, or contact an administrator.
              </Text>
            </Stack>
          }
        />
      </Center>
    );
  }

  // All other errors
  return (
    <Center flex={1} data-testid={testId}>
      <ObjectNotFound
        icon={CiFolderOff}
        title="Directory not found"
        subtitle={
          <Stack>
            <Text>The directory doesn't exist or may have be deleted.</Text>
            <Text>Please contact an administrator for further assistance.</Text>
          </Stack>
        }
      />
    </Center>
  );
};
